/** Color helpers for tinting UI from ingest-extracted artwork colors. */

function clamp(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

export function rgbToHex([r, g, b]: [number, number, number]): string {
  return `#${((1 << 24) | (clamp(r) << 16) | (clamp(g) << 8) | clamp(b)).toString(16).slice(1)}`;
}

/** Mix a color toward another. t=0 → a, t=1 → b. */
export function mix(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  if (!ca || !cb) return a;
  return rgbToHex([
    ca[0] + (cb[0] - ca[0]) * t,
    ca[1] + (cb[1] - ca[1]) * t,
    ca[2] + (cb[2] - ca[2]) * t
  ]);
}

function luminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
}

/**
 * Lift an artwork color into the app's pastel accent register: bright enough
 * for dark ink on top, saturated enough to still feel like the artwork.
 */
export function pastelize(hex: string): string {
  let color = mix(hex, "#FFFFFF", 0.35);
  // Guarantee ink contrast on very dark art colors.
  while (luminance(color) < 0.62) {
    color = mix(color, "#FFFFFF", 0.18);
  }
  return color;
}

/**
 * Button/accent gradient stops from ingest artColors; falls back to null when
 * artwork colors aren't available (caller uses the brand gradient).
 */
export function artGradient(artColors: string[] | undefined | null): [string, string, string] | null {
  if (!artColors || artColors.length < 2) return null;
  const stops = artColors.slice(0, 3).map(pastelize);
  if (stops.length === 2) stops.push(stops[0]);
  return [stops[0], stops[1], stops[2]];
}
